/**
 * Analytics Client Layer
 * 
 * Simplified analytics tracking API for components.
 * Handles session management, button tracking, cue interactions,
 * and sends data to Neon database.
 * 
 * USAGE IN COMPONENTS:
 * import { trackButton, trackCue, trackPage, startSession } from '@/lib/analytics/client'
 * 
 * - trackButton('start_here_button', { navigation_source: 'toc' })
 * - trackCue('Golden Leaf', 1, 7)
 * - trackPage(1, 5, 'chapter_nav')
 * - startSession() // Called once when book opens
 */

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface AnalyticsState {
  currentSessionId: string | null;
  sessionStartTime: number;
  pagesVisitedInSession: Set<number>;
  interactionsInSession: string[];
  
  currentPageType: "story" | "cue" | "activity" | "navigation" | "other";
  navigationSource: "toc" | "chapter_nav" | "spread_nav" | "breadcrumb" | "home_button" | "direct_url" | "other";
  pageStartTime: number;
  firstInteractionTime: number;
  cuePageStartTime: number;
  
  currentPageInteractions: Array<{
    type: "click" | "scroll" | "hover" | "focus" | "play" | "pause" | "print" | "cue_click" | "nav_click";
    element: string;
    timestamp: string;
    position?: { x: number; y: number };
    metadata?: {
      navigation_source?: "toc" | "chapter_nav" | "spread_nav" | "breadcrumb" | "home_button" | "direct_url" | "other";
      cue_name?: string;
      print_target?: string;
    };
  }>;
}

// ============================================================================
// ANALYTICS STATE
// ============================================================================

let analyticsState: AnalyticsState = {
  currentSessionId: null,
  sessionStartTime: 0,
  pagesVisitedInSession: new Set(),
  interactionsInSession: [],
  
  currentPageType: "story",
  navigationSource: "direct_url",
  pageStartTime: 0,
  firstInteractionTime: 0,
  cuePageStartTime: 0,
  currentPageInteractions: [],
};

// ============================================================================
// HELPER FUNCTIONS (INTERNAL)
// ============================================================================

/**
 * Get auth token for API calls from sessionStorage only
 */
function getAuthToken(): string | null {
  return sessionStorage.getItem('auth_token');
}

/**
 * Detect page type based on chapter/spread and URL
 */
function detectPageType(chapter: number, spread: number, url?: string): "story" | "cue" | "activity" | "navigation" | "other" {
  if (url) {
    if (url.includes('/activity')) return "activity";
    if (url.includes('/introduction') || url.includes('/conclusion') || url.includes('/wtbtg')) return "navigation";
  }
  
  const cuePages = {
    1: 7,
    2: 7,
    3: 6,
    4: 6,
    5: 8,
    6: 7,
  };
  
  if (cuePages[chapter as keyof typeof cuePages] === spread) {
    return "cue";
  }
  
  return "story";
}

/**
 * Detect navigation source from referrer info
 */
function detectNavigationSource(referrerInfo?: string): "toc" | "chapter_nav" | "spread_nav" | "breadcrumb" | "home_button" | "direct_url" | "other" {
  if (!referrerInfo) return "direct_url";
  
  if (referrerInfo.includes('toc') || referrerInfo.includes('contents')) return "toc";
  if (referrerInfo.includes('chapter-nav')) return "chapter_nav";
  if (referrerInfo.includes('spread-nav') || referrerInfo.includes('arrow')) return "spread_nav";
  if (referrerInfo.includes('breadcrumb')) return "breadcrumb";
  if (referrerInfo.includes('home') || referrerInfo.includes('back')) return "home_button";
  
  return "other";
}

/**
 * Get current chapter name
 */
function getCurrentChapterName(): string {
  const chapterNames = [
    "Introduction",
    "The Still Pond",
    "The Colorful Chameleon", 
    "The Brave Bridge",
    "The Kindness Meadow",
    "The Gratitude Garden",
    "The Sharing Tree",
    "Conclusion"
  ];
  
  return chapterNames[analyticsState.currentPageInteractions.length] || "Unknown Chapter";
}

// ============================================================================
// SESSION MANAGEMENT
// ============================================================================

// REQUEST DEDUPLICATION: Synchronous flag prevents race conditions
let sessionCreationInProgress = false;

/**
 * Start a reading session with 60-minute reuse logic + request deduplication
 */
export async function startSession(): Promise<string | null> {
  const now = Date.now();
  const SIXTY_MINUTES = 60 * 60 * 1000;

  // Check for existing valid session first
  if (analyticsState.currentSessionId && analyticsState.sessionStartTime > 0) {
    const sessionAge = now - analyticsState.sessionStartTime;
    const sessionAgeMinutes = Math.round(sessionAge / 60000);
    
    if (sessionAge < SIXTY_MINUTES) {
      console.log('[Analytics] REUSING memory session:', {
        sessionId: analyticsState.currentSessionId,
        ageMinutes: sessionAgeMinutes,
        remainingMinutes: Math.round((SIXTY_MINUTES - sessionAge) / 60000)
      });
      return analyticsState.currentSessionId;
    } else {
      console.log('[Analytics] Session expired, ending:', {
        sessionId: analyticsState.currentSessionId,
        ageMinutes: sessionAgeMinutes
      });
      await endSession(analyticsState.currentSessionId, {
        totalDuration: Math.floor(sessionAge / 1000),
        pagesVisited: Array.from(analyticsState.pagesVisitedInSession),
        finalInteractions: analyticsState.interactionsInSession.length,
        cuesCollected: []
      });
      analyticsState.currentSessionId = null;
      analyticsState.sessionStartTime = 0;
    }
  }
  
  // Deduplication check
  if (sessionCreationInProgress) {
    console.log('[Analytics] Session creation in progress, waiting...');
    while (sessionCreationInProgress) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return analyticsState.currentSessionId;
  }
  
  sessionCreationInProgress = true;
  
  try {
    console.log('[Analytics] Starting session creation...');
    
    const authToken = getAuthToken();

    // Check Neon for existing active session (handles tab close/reopen)
    if (authToken) {
      try {
        const tokenParts = authToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        const userId = payload.userId || payload.sub;
        const bookId = 'wtbtg';
        
        console.log('[Analytics] Checking Neon for active session...', { userId, bookId });
        
        const response = await fetch(
          `/api/book-access/analytics/active-session/${userId}/${bookId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const activeSession = await response.json();
          
          if (activeSession && activeSession.id) {
            const sessionStart = new Date(activeSession.session_start).getTime();
            const sessionAge = now - sessionStart;
            const sessionAgeMinutes = Math.round(sessionAge / 60000);
            
            console.log('[Analytics] REUSING Neon session:', {
              sessionId: activeSession.id,
              ageMinutes: sessionAgeMinutes,
              remainingMinutes: Math.round((SIXTY_MINUTES - sessionAge) / 60000)
            });
            
            analyticsState.currentSessionId = activeSession.id;
            analyticsState.sessionStartTime = sessionStart;
            analyticsState.pagesVisitedInSession = new Set(activeSession.pages_visited || []);
            
            return activeSession.id;
          }
        }
      } catch (neonCheckError) {
        console.warn('[Analytics] Neon check failed:', neonCheckError);
      }
    }
    
    // Create new session with retry logic for Neon cold start
    console.log('[Analytics] Creating new session...');
    
    if (!authToken) {
      console.error('[Analytics] No auth token - cannot create session');
      return null;
    }

    let sessionId: string | null = null;
    const MAX_RETRIES = 4;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const delay = attempt * 8000; // 8s, 16s, 24s - give server time to complete its own retries
        console.log(`[Analytics] Retry attempt ${attempt + 1}/${MAX_RETRIES} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      try {
        const response = await fetch('/api/book-access/analytics/start-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            bookId: 'wtbtg',
            device_type: 'web',
            browser_info: navigator.userAgent
          })
        });

        if (response.ok) {
          const data = await response.json();
          sessionId = data.sessionId;

          if (sessionId) {
            analyticsState.currentSessionId = sessionId;
            analyticsState.sessionStartTime = now;
            analyticsState.pagesVisitedInSession = new Set();
            analyticsState.interactionsInSession = [];

            console.log('[Analytics] Session created:', {
              sessionId,
              attempt: attempt + 1,
              validFor: '60 minutes',
              expiresAt: new Date(now + SIXTY_MINUTES).toISOString()
            });

            break; // Success - exit retry loop
          } else {
            console.warn(`[Analytics] No session ID returned on attempt ${attempt + 1}`);
          }
        } else {
          const errorText = await response.text();
          console.warn(`[Analytics] Session attempt ${attempt + 1} failed:`, response.status, errorText);
        }
      } catch (fetchError) {
        console.warn(`[Analytics] Session fetch error attempt ${attempt + 1}:`, fetchError);
      }
    }

    if (!sessionId) {
      console.error('[Analytics] All session creation attempts failed');
    }

    return sessionId;

  } catch (error) {
    console.error('[Analytics] Failed to start session:', error);
    return null;
  } finally {
    sessionCreationInProgress = false;
  }
}

/**
 * End a reading session
 */
export async function endSession(sessionId: string, finalMetrics: {
  totalDuration: number;
  pagesVisited: number[];
  finalInteractions: number;
  cuesCollected: string[];
}): Promise<void> {
  try {
    console.log('[Analytics] Ending session:', sessionId);
    
    if (analyticsState.currentSessionId !== sessionId) {
      console.warn('[Analytics] Session ID mismatch, not ending');
      return;
    }

    const authToken = getAuthToken();
    if (authToken) {
      try {
        const response = await fetch('/api/book-access/analytics/end-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            sessionId: sessionId,
            final_metrics: finalMetrics
          })
        });

        if (response.ok) {
          console.log('[Analytics] Session ended');
        }
      } catch (error) {
        console.warn('[Analytics] Failed to end session:', error);
      }
    }

    analyticsState.currentSessionId = null;
    analyticsState.sessionStartTime = 0;
    analyticsState.pagesVisitedInSession.clear();
    analyticsState.interactionsInSession = [];
  } catch (error) {
    console.error('[Analytics] Failed to end session:', error);
  }
}

/**
 * Get the current session ID (for logout cleanup)
 */
export function getCurrentSessionId(): string | null {
  return analyticsState.currentSessionId;
}

// ============================================================================
// PAGE TRACKING
// ============================================================================

/**
 * Start timing for a new page
 */
function startPageTiming(chapter: number, spread: number, navigationSource?: string): void {
  const now = Date.now();
  
  analyticsState.pageStartTime = now;
  analyticsState.firstInteractionTime = 0;
  analyticsState.currentPageInteractions = [];
  
  analyticsState.currentPageType = detectPageType(chapter, spread, window.location.href);
  analyticsState.navigationSource = detectNavigationSource(navigationSource);
  
  if (analyticsState.currentPageType === "cue") {
    analyticsState.cuePageStartTime = now;
    console.log('[Analytics] Cue page timing started:', {
      chapter,
      spread,
      pageType: analyticsState.currentPageType,
      startTime: new Date(now).toISOString()
    });
  }
  
  console.log('[Analytics] Page timing started:', {
    chapter,
    spread,
    pageType: analyticsState.currentPageType,
    navigationSource: analyticsState.navigationSource,
    timestamp: new Date(now).toISOString()
  });
}

/**
 * Track page view and send analytics
 */
export async function trackPage(
  chapter: number, 
  spread: number, 
  navigationSource?: string
): Promise<void> {
  startPageTiming(chapter, spread, navigationSource);
  
  if (analyticsState.currentSessionId) {
    const pageNumber = ((chapter - 1) * 9) + spread;
    analyticsState.pagesVisitedInSession.add(pageNumber);
    analyticsState.interactionsInSession.push(`page_visit_${chapter}_${spread}`);
    
    console.log('[Analytics] Page visit tracked:', {
      sessionId: analyticsState.currentSessionId,
      chapter,
      spread,
      pageType: analyticsState.currentPageType,
      totalPagesInSession: analyticsState.pagesVisitedInSession.size
    });
  }
}

// ============================================================================
// INTERACTION TRACKING
// ============================================================================

/**
 * Track button click
 */
export async function trackButton(
  buttonId: string, 
  metadata?: {
    navigation_source?: "toc" | "chapter_nav" | "spread_nav" | "breadcrumb" | "home_button" | "direct_url" | "other";
    position?: { x: number; y: number };
  }
): Promise<void> {
  const now = Date.now();
  
  if (analyticsState.firstInteractionTime === 0) {
    analyticsState.firstInteractionTime = now;
    console.log('[Analytics] First interaction:', {
      button: buttonId,
      timeFromPageStart: Math.round((now - analyticsState.pageStartTime) / 1000) + 's'
    });
  }
  
  const interaction = {
    type: "nav_click" as const,
    element: buttonId,
    timestamp: new Date(now).toISOString(),
    position: metadata?.position,
    metadata: {
      navigation_source: metadata?.navigation_source,
    }
  };
  
  analyticsState.currentPageInteractions.push(interaction);
  
  console.log('[Analytics] Button tracked:', {
    button: buttonId,
    pageType: analyticsState.currentPageType,
    totalInteractions: analyticsState.currentPageInteractions.length
  });
}

/**
 * Track cue interaction
 */
export function trackCue(
  cueName: string, 
  chapter: number, 
  spread: number
): {
  cue_name: string;
  chapter_id: number;
  spread_number: number;
  time_before_click: number;
  click_timestamp: string;
  engagement_score: number;
} {
  const now = Date.now();
  const timeBeforeClick = Math.round((now - analyticsState.cuePageStartTime) / 1000);
  
  let engagementScore = 50;
  if (timeBeforeClick >= 10) engagementScore += 30;
  if (timeBeforeClick >= 20) engagementScore += 20;
  engagementScore = Math.min(100, engagementScore);
  
  const cueInteraction = {
    cue_name: cueName,
    chapter_id: chapter,
    spread_number: spread,
    time_before_click: timeBeforeClick,
    click_timestamp: new Date(now).toISOString(),
    engagement_score: engagementScore,
  };
  
  console.log('[Analytics] Cue tracked:', {
    cueName,
    chapter,
    spread,
    timeBeforeClick: timeBeforeClick + 's',
    engagementScore
  });
  
  trackButton(`cue-${cueName}`, { position: { x: 0, y: 0 } });
  
  return cueInteraction;
}

/**
 * Track print button click
 */
export function trackPrint(printTarget: string): {
  print_clicks: number;
  print_targets: string[];
  time_on_activity_page: number;
} {
  const now = Date.now();
  const timeOnActivityPage = Math.round((now - analyticsState.pageStartTime) / 1000);
  
  const printData = {
    print_clicks: 1,
    print_targets: [printTarget],
    time_on_activity_page: timeOnActivityPage,
  };
  
  console.log('[Analytics] Print tracked:', {
    printTarget,
    timeOnPage: timeOnActivityPage + 's',
    pageType: analyticsState.currentPageType
  });
  
  trackButton(`print-${printTarget}`);
  
  return printData;
}

// ============================================================================
// SEND TO NEON
// ============================================================================

/**
 * Send analytics data to Neon database
 */
export async function sendAnalytics(
  chapter: number, 
  spread: number, 
  cueInteractions?: any[], 
  printData?: any
): Promise<boolean> {
  try {
    const authToken = getAuthToken();
    
    if (!authToken || analyticsState.currentPageInteractions.length === 0) {
      console.log('[Analytics] Skipping - no auth or no interactions');
      return true;
    }
    
    let sessionId = analyticsState.currentSessionId;
    if (!sessionId) {
      console.log('[Analytics] No active session - creating session first...');
      sessionId = await startSession();
      
      if (!sessionId) {
        console.error('[Analytics] Failed to create session');
        return false;
      }
      
      console.log('[Analytics] Session created:', sessionId);
    }
    
    const now = Date.now();
    const timeOnPage = Math.round((now - analyticsState.pageStartTime) / 1000);
    const actualEngagementTime = analyticsState.firstInteractionTime > 0 
      ? Math.round((now - analyticsState.firstInteractionTime) / 1000)
      : timeOnPage;
    const timeBeforeFirstInteraction = analyticsState.firstInteractionTime > 0 
      ? Math.round((analyticsState.firstInteractionTime - analyticsState.pageStartTime) / 1000)
      : 0;
    const sessionDurationSoFar = Math.round((now - analyticsState.sessionStartTime) / 1000);
    
    const analyticsData = {
      session_id: sessionId,
      bookId: 'wtbtg',
      
      page_data: {
        page_number: ((chapter - 1) * 9) + spread,
        chapter_id: chapter,
        chapter_name: getCurrentChapterName(),
        page_type: analyticsState.currentPageType,
        navigation_source: analyticsState.navigationSource,
      },
      
      timing_data: {
        time_on_page: timeOnPage,
        actual_engagement_time: actualEngagementTime,
        time_before_first_interaction: timeBeforeFirstInteraction,
        session_duration_so_far: sessionDurationSoFar,
      },
      
      interactions: analyticsState.currentPageInteractions,
      cue_interactions: cueInteractions || [],
      print_data: printData ? {
        printClicks: printData.print_clicks,
        printTargets: printData.print_targets,
        timeOnActivityPage: printData.time_on_activity_page,
      } : undefined,
    };
    
    console.log('[Analytics] SENDING:', {
      sessionId: sessionId,
      pageType: analyticsState.currentPageType,
      navigationSource: analyticsState.navigationSource,
      timeOnPage: timeOnPage + 's',
      interactions: analyticsState.currentPageInteractions.length,
    });

    try {
      const response = await fetch('/api/book-access/analytics/track-enhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(analyticsData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('[Analytics] Sent to Neon:', {
          sessionId: sessionId,
          pageType: analyticsState.currentPageType,
          processed: result.analytics_processed,
        });
        return true;
      } else {
        const errorText = await response.text();
        console.warn('[Analytics] Failed:', response.status, errorText);
        return false;
      }
    } catch (neonError) {
      console.warn('[Analytics] API call failed:', neonError);
      return false;
    }
  } catch (error) {
    console.error('[Analytics] Error:', error);
    return false;
  }
}
